/**
 * SignaturePad - firma digitale del cliente su canvas (mouse + touch).
 *
 * Props:
 *   - value: data URL della firma corrente (controllato)
 *   - onChange(dataUrl): callback quando la firma viene salvata
 *   - label: etichetta (default 'Firma del cliente')
 *   - height: altezza del canvas (default 160)
 */
import { useRef, useState, useEffect } from 'react';
import { Eraser, Check } from 'lucide-react';
import { Button } from './ui/button';

export const SignaturePad = ({ value, onChange, label = 'Firma del cliente', height = 160 }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(Boolean(value));
  const initialValueRef = useRef(value);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';

    // Restore existing signature (solo valore iniziale, al mount)
    const initial = initialValueRef.current;
    if (initial) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = initial;
      setHasDrawn(true);
    }
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stop = () => {
    if (drawing) {
      setDrawing(false);
      // Auto-salva la firma al rilascio del tratto (evita perdita se si dimentica 'Conferma')
      if (canvasRef.current) {
        const dataUrl = canvasRef.current.toDataURL('image/png');
        onChange && onChange(dataUrl);
      }
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onChange && onChange(null);
  };

  const save = () => {
    if (!hasDrawn) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onChange && onChange(dataUrl);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clear}
            className="rounded-md gap-2 text-xs"
            data-testid="signature-clear"
          >
            <Eraser className="h-3.5 w-3.5" /> Cancella
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={save}
            disabled={!hasDrawn}
            className="rounded-md gap-2 text-xs"
            data-testid="signature-save"
          >
            <Check className="h-3.5 w-3.5" /> Conferma
          </Button>
        </div>
      </div>
      <div
        className="relative rounded-md border-2 border-dashed border-border bg-card overflow-hidden"
        style={{ height: `${height}px`, touchAction: 'none' }}
      >
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-crosshair"
          style={{ width: '100%', height: '100%' }}
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={stop}
          data-testid="signature-canvas"
        />
        {!hasDrawn && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-sm text-muted-foreground italic">Firma qui sopra</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignaturePad;
